import type { ConfigurableNodeFields } from '../../types/nodeConfig';
import { NODE_TYPE_LABELS } from '../../lib/nodeConfig';
import { resolveNodeTypeAppearance } from '../../lib/nodeVisualGeometry';
import { useEditorStore } from '../../store/editorStore';
import type { NodeType } from '../../types/project';

interface Props {
  node: ConfigurableNodeFields;
  readOnly?: boolean;
  onChange: (patch: Partial<ConfigurableNodeFields>) => void;
}

export default function BasicNodeFields({ node, readOnly, onChange }: Props) {
  const type = node.type as NodeType;
  const settings = useEditorStore((s) => s.document?.settings);
  const appearance = resolveNodeTypeAppearance(type, settings);

  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Basic properties</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-text-muted">Name</label>
          <input
            type="text"
            className="input-field w-full"
            value={node.name}
            disabled={readOnly}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">Type</label>
          <p className="text-sm text-text">{type}</p>
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">Group</label>
          <input
            type="text"
            className="input-field w-full"
            value={node.group ?? ''}
            disabled={readOnly}
            onChange={(e) => onChange({ group: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">Type appearance</label>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
            <span
              className="h-5 w-5 shrink-0 rounded-full ring-1 ring-white/15"
              style={{ backgroundColor: appearance.color }}
              aria-hidden
            />
            <span className="text-sm text-text-muted">
              {NODE_TYPE_LABELS[type]} · {appearance.radius}px · {appearance.shape}
              {' '}— edit in Node Palette
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs text-text-muted">X</label>
            <input
              type="number"
              className="input-field w-full"
              value={Math.round(node.x)}
              readOnly
              disabled
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-text-muted">Y</label>
            <input
              type="number"
              className="input-field w-full"
              value={Math.round(node.y)}
              readOnly
              disabled
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-text-muted">Notes</label>
          <textarea
            className="input-field min-h-[72px] w-full resize-y"
            value={node.notes ?? ''}
            disabled={readOnly}
            onChange={(e) => onChange({ notes: e.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={node.enabled !== false}
            disabled={readOnly}
            onChange={(e) => onChange({ enabled: e.target.checked })}
          />
          Enabled
        </label>
        <label className="flex items-center gap-2 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={node.display_label !== false}
            disabled={readOnly}
            onChange={(e) => onChange({ display_label: e.target.checked })}
          />
          Display label on canvas
        </label>
        <label className="flex items-center gap-2 text-sm text-text-muted sm:col-span-2">
          <input
            type="checkbox"
            checked={Boolean(node.show_in_simulation_controls)}
            disabled={readOnly}
            onChange={(e) =>
              onChange({ show_in_simulation_controls: e.target.checked })
            }
          />
          Show in simulation controls
        </label>
      </div>
    </section>
  );
}
